/**
 * RecentlyHiredRibbon — companion to LiveJobsTicker.
 *
 * Real hires (from `job_applications.status='hired'` with explicit
 * share permission), anonymized to first-name + last-initial. Renders
 * silently as null when no share-permitted hires exist so the homepage
 * never shows fabricated activity.
 */
import React, { useEffect, useState } from "react";
import { Trophy, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";

const timeAgo = (iso) => {
    if (!iso) return "";
    try {
        const diff = (Date.now() - new Date(iso).getTime()) / 1000;
        if (diff < 60) return "just now";
        if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
        return `${Math.round(diff / 86400)}d ago`;
    } catch { return ""; }
};

const HirePill = ({ hire, idx }) => (
    <div
        data-testid={`home-hires-ribbon-item-${idx}`}
        className="group inline-flex items-center gap-3 rounded-full border border-border bg-card px-4 py-2 shrink-0"
    >
        <span className="inline-flex items-center gap-1 rounded-full bg-sky-500/15 text-sky-700 dark:text-sky-400 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
            <Trophy size={10} /> Hired
        </span>
        <span className="text-sm font-semibold text-foreground whitespace-nowrap">
            {hire.display_name}
        </span>
        <span className="text-xs text-muted-foreground whitespace-nowrap">
            {hire.job_title}{hire.company ? ` · ${hire.company}` : ""}
        </span>
        <span className="text-[10px] text-muted-foreground/70 whitespace-nowrap">
            {timeAgo(hire.hired_at)}
        </span>
    </div>
);

export default function RecentlyHiredRibbon() {
    const [hires, setHires] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        api.get("/hires/recent", { params: { limit: 3 } })
            .then((r) => { if (mounted) setHires(r.data?.hires || []); })
            .catch(() => { if (mounted) setHires([]); })
            .finally(() => { if (mounted) setLoading(false); });
        return () => { mounted = false; };
    }, []);

    if (loading || !hires.length) return null;
    const strip = [...hires, ...hires];

    return (
        <section
            className="relative border-b border-border bg-sky-500/5 py-3 overflow-hidden"
            aria-label="Recent hires through Simonara"
            data-testid="home-hires-ribbon"
        >
            <div className="container-page flex items-center gap-3">
                <div className="hidden md:inline-flex items-center gap-1 shrink-0 text-[11px] font-semibold uppercase tracking-wider text-sky-700 dark:text-sky-400">
                    <Trophy size={12} /> Recently hired
                </div>
                <div className="relative flex-1 overflow-hidden group">
                    <div
                        className="flex items-center gap-3 animate-[marquee_36s_linear_infinite] group-hover:[animation-play-state:paused]"
                        style={{ willChange: "transform" }}
                        data-testid="home-hires-ribbon-strip"
                    >
                        {strip.map((h, i) => (
                            <HirePill key={`${h.id}-${i}`} hire={h} idx={i % hires.length} />
                        ))}
                    </div>
                    <div className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-sky-500/10 to-transparent" />
                    <div className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-sky-500/10 to-transparent" />
                </div>
                <Link
                    to="/success-stories"
                    data-testid="home-hires-ribbon-stories-link"
                    className="hidden sm:inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-sky-700 dark:text-sky-400 hover:underline"
                >
                    Success stories <ArrowRight size={11} />
                </Link>
            </div>
        </section>
    );
}
